import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RecipeSchedules } from "@/components/RecipeSchedules";
import {
  X, Zap, Plus, Trash2, Play, Loader2, Webhook, Search, ShieldCheck, Wallet, Check,
  MessageSquare, Send, Phone, FileText, SquareKanban, Users, Ticket, Megaphone, Mail,
  Share2, Github, Table2, Database, HardDrive, Cloud, CreditCard, DollarSign, ShoppingCart,
} from "lucide-react";

const ICONS = { MessageSquare, Send, Phone, FileText, SquareKanban, Users, Ticket, Megaphone, Mail, Share2, Github, Table2, Database, HardDrive, Cloud, CreditCard, DollarSign, ShoppingCart, Zap, Webhook };
const Icon = ({ name, size = 16 }) => { const C = ICONS[name] || Webhook; return <C size={size} />; };

const TABS = [
  { key: "recipes", label: "Recipes" },
  { key: "connected", label: "Connected" },
  { key: "schedules", label: "Schedules" },
  { key: "queue", label: "Agent Queue" },
];

export default function AutomationsPanel({ onClose }) {
  const [tab, setTab] = useState("recipes");
  const [recipes, setRecipes] = useState([]);
  const [items, setItems] = useState(null);
  const [queue, setQueue] = useState([]);
  const [wallet, setWallet] = useState(null);
  const [search, setSearch] = useState("");
  const [cat, setCat] = useState("All");
  const [cfg, setCfg] = useState(null); // recipe being configured
  const [form, setForm] = useState({ name: "", url: "", description: "" });
  const [connecting, setConnecting] = useState(false);
  const [running, setRunning] = useState(null);
  const [otp, setOtp] = useState(null); // { mode:'run'|'queue', id, code, input, label }
  const [busy, setBusy] = useState(false);
  const [scheduleId, setScheduleId] = useState("");

  const loadItems = () => api.getAutomations().then(setItems).catch(() => setItems([]));
  const loadQueue = () => api.getAutomationQueue().then(setQueue).catch(() => setQueue([]));
  const loadWallet = () => api.getWallet().then(setWallet).catch(() => {});

  useEffect(() => {
    api.getRecipes().then(setRecipes).catch(() => setRecipes([]));
    loadItems(); loadQueue(); loadWallet();
  }, []);

  const cats = useMemo(() => ["All", ...Array.from(new Set(recipes.map((r) => r.category)))], [recipes]);
  const filtered = useMemo(() => recipes.filter((r) =>
    (cat === "All" || r.category === cat) &&
    (!search.trim() || `${r.name} ${r.category} ${r.description} ${(r.tags || []).join(" ")}`.toLowerCase().includes(search.toLowerCase()))
  ), [recipes, cat, search]);

  const openConfig = (r) => { setCfg(r); setForm({ name: r.name, url: "", description: r.description }); };

  const connect = async () => {
    if (!form.url.trim() || !form.url.startsWith("http")) { toast.error("Enter a valid webhook URL"); return; }
    setConnecting(true);
    try {
      const payload = { name: form.name || cfg.name, url: form.url, description: form.description,
        recipe_key: cfg.key, category: cfg.category, icon: cfg.icon, color: cfg.color, tags: cfg.tags, payload_hint: cfg.payload_hint, risk: cfg.risk };
      const a = await api.createAutomation(payload);
      setItems((p) => [a, ...(p || [])]);
      toast.success(`Connected ${cfg.name}`);
      setCfg(null); setTab("connected");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not connect"); }
    finally { setConnecting(false); }
  };

  const runTest = async (a) => {
    setRunning(a.id);
    try {
      const r = await api.runAutomation(a.id);
      if (r.needs_otp) { setOtp({ mode: "run", id: a.id, code: r.code, input: "", label: a.name }); }
      else if (r.ok) toast.success(`Fired "${a.name}" (${r.status_code})`);
      else toast.error(`"${a.name}" failed (${r.status_code || "no response"})`);
      loadItems();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setRunning(null); }
  };

  const removeItem = async (a) => { await api.deleteAutomation(a.id); setItems((p) => p.filter((x) => x.id !== a.id)); };

  const approve = async (q) => {
    setBusy(true);
    try {
      const r = await api.approveAutomationQueue(q.id);
      if (r.needs_otp) { setOtp({ mode: "queue", id: q.id, code: r.code, input: "", label: q.automation_name }); }
      else if (r.ok) { toast.success(`Fired "${q.automation_name}"`); loadQueue(); loadWallet(); }
      else { toast.error(`"${q.automation_name}" failed`); loadQueue(); }
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const dismiss = async (q) => { await api.dismissAutomationQueue(q.id); setQueue((p) => p.filter((x) => x.id !== q.id)); };

  const confirmOtp = async () => {
    if (!otp) return;
    setBusy(true);
    try {
      const call = otp.mode === "run" ? api.runAutomation(otp.id, undefined, otp.input) : api.approveAutomationQueue(otp.id, otp.input);
      const r = await call;
      if (r.ok) { toast.success(`Authorized & fired "${otp.label}"`); setOtp(null); loadItems(); loadQueue(); loadWallet(); }
      else toast.error("Fired but downstream failed");
    } catch (e) { toast.error(e.response?.data?.detail || "Invalid code"); }
    finally { setBusy(false); }
  };

  return (
    <div className="max-w-5xl mx-auto pt-8 px-4 pb-24">
      <div className="flex items-center justify-between mb-2">
        <h2 className="font-display uppercase tracking-widest text-cyan-300 text-lg flex items-center gap-2"><Zap size={18} /> Automations</h2>
        <button data-testid="automations-close-button" onClick={onClose} className="w-8 h-8 grid place-items-center rounded-lg bg-white/5 hover:bg-white/10 text-zinc-400"><X size={16} /></button>
      </div>
      <p className="text-zinc-500 font-mono text-xs mb-5">Pick a one-click recipe, paste a webhook URL, and your agents can trigger 1000s of downstream actions — Slack, Sheets, CRM, Notion, payments and more.</p>

      <div className="flex items-center gap-1 mb-6 bg-white/[0.03] rounded-xl p-1 border border-white/5 w-fit">
        {TABS.map((t) => (
          <button key={t.key} data-testid={`automations-tab-${t.key}`} onClick={() => setTab(t.key)}
            className={`px-3.5 py-1.5 rounded-lg text-[11px] font-tactical font-semibold uppercase tracking-wider transition-colors ${tab === t.key ? "bg-cyan-500/15 text-cyan-300 border border-cyan-400/30" : "text-zinc-400 hover:text-zinc-200"}`}>
            {t.label}{t.key === "connected" && items ? ` (${items.length})` : ""}{t.key === "queue" && queue.length ? ` (${queue.length})` : ""}
          </button>
        ))}
      </div>

      {/* RECIPES */}
      {tab === "recipes" && (
        <div className="anim-slidein">
          <div className="flex flex-col sm:flex-row gap-3 mb-4">
            <div className="relative flex-1">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
              <Input data-testid="recipe-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search 39 recipes — slack, crm, sheets, payments…" className="bg-[#0b0e14] border-white/10 text-zinc-100 pl-9" />
            </div>
          </div>
          <div className="flex flex-wrap gap-1.5 mb-5">
            {cats.map((c) => (
              <button key={c} data-testid={`recipe-cat-${c}`} onClick={() => setCat(c)}
                className={`px-3 py-1 rounded-full text-[11px] font-tactical font-semibold uppercase tracking-wider border transition-colors ${cat === c ? "bg-cyan-500/15 text-cyan-300 border-cyan-400/40" : "bg-white/[0.02] text-zinc-400 border-white/10 hover:text-zinc-200"}`}>{c}</button>
            ))}
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {filtered.map((r) => (
              <div key={r.key} data-testid={`recipe-card-${r.key}`} className="group rounded-xl border border-white/10 bg-white/[0.02] p-4 hover:border-cyan-400/30 hover:bg-white/[0.04] transition-colors flex flex-col">
                <div className="flex items-start gap-3 mb-2">
                  <div className="w-10 h-10 rounded-lg grid place-items-center shrink-0" style={{ backgroundColor: `${r.color}22`, border: `1px solid ${r.color}55`, color: r.color }}><Icon name={r.icon} size={18} /></div>
                  <div className="min-w-0">
                    <div className="text-sm text-zinc-100 font-medium leading-tight">{r.name}</div>
                    <div className="text-[10px] uppercase tracking-wider text-zinc-500 font-tactical mt-0.5">{r.category}</div>
                  </div>
                  {r.risk === "high" && <span className="ml-auto text-[9px] font-tactical font-bold uppercase text-amber-300 bg-amber-500/10 border border-amber-400/30 rounded px-1.5 py-0.5 flex items-center gap-1"><ShieldCheck size={10} /> OTP</span>}
                </div>
                <p className="text-xs text-zinc-500 leading-snug flex-1 mb-3">{r.description}</p>
                <Button data-testid={`recipe-use-${r.key}`} onClick={() => openConfig(r)} className="w-full h-8 bg-white/5 hover:bg-cyan-500 hover:text-[#04070c] text-cyan-300 border border-cyan-400/20 font-tactical font-bold uppercase text-[11px] transition-colors"><Plus size={13} className="mr-1" /> Use recipe</Button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* CONNECTED */}
      {tab === "connected" && (
        <div className="anim-slidein">
          {items && items.length === 0 && <div className="text-center text-zinc-600 font-mono text-sm py-12">No automations connected yet. Pick a recipe to get started.</div>}
          <div className="space-y-2">
            {(items || []).map((a) => (
              <div key={a.id} data-testid="automation-item" className="rounded-xl border border-white/10 bg-white/[0.02] p-3 flex items-center gap-3">
                <div className="w-9 h-9 rounded-lg grid place-items-center shrink-0" style={{ backgroundColor: `${a.color || "#22D3EE"}22`, border: `1px solid ${a.color || "#22D3EE"}55`, color: a.color || "#22D3EE" }}><Icon name={a.icon} size={16} /></div>
                <div className="min-w-0 flex-1">
                  <div className="text-sm text-zinc-100 truncate font-medium flex items-center gap-2">{a.name}{a.risk === "high" && <ShieldCheck size={12} className="text-amber-300" />}</div>
                  <div className="text-[11px] font-mono text-zinc-500 truncate">{a.description || a.url} · {a.runs || 0} runs</div>
                </div>
                <button data-testid={`automation-schedule-${a.id}`} onClick={() => { setScheduleId(a.id); setTab("schedules"); }} className="h-8 px-2 rounded-lg bg-cyan-500/10 hover:bg-cyan-500/20 text-cyan-300 text-xs font-tactical uppercase">Schedule</button>
                <button data-testid="automation-run" onClick={() => runTest(a)} disabled={running === a.id} className="h-8 px-3 rounded-lg bg-emerald-500/15 hover:bg-emerald-500/25 text-emerald-300 border border-emerald-400/30 text-xs font-tactical font-bold uppercase flex items-center gap-1">
                  {running === a.id ? <Loader2 size={13} className="animate-spin" /> : <><Play size={12} /> Test</>}
                </button>
                <button data-testid="automation-delete" onClick={() => removeItem(a)} className="w-8 h-8 grid place-items-center rounded-lg bg-white/5 hover:bg-red-500/10 text-red-400"><Trash2 size={15} /></button>
              </div>
            ))}
          </div>
        </div>
      )}

      {tab === "schedules" && <RecipeSchedules items={items} selectedId={scheduleId} onRecipes={() => setTab("recipes")} />}

      {/* AGENT QUEUE */}
      {tab === "queue" && (
        <div className="anim-slidein">
          <div className="rounded-xl border border-cyan-500/20 bg-cyan-500/[0.03] p-4 mb-4 flex items-center gap-3" data-testid="sandbox-wallet">
            <div className="w-10 h-10 rounded-lg grid place-items-center bg-cyan-500/10 border border-cyan-400/30 text-cyan-300"><Wallet size={18} /></div>
            <div className="flex-1">
              <div className="text-[10px] uppercase tracking-wider text-zinc-500 font-tactical">Sandbox Wallet · demo funds only</div>
              <div className="text-lg font-display text-cyan-200">{wallet ? `$${wallet.balance.toFixed(2)} ${wallet.currency}` : "—"}</div>
            </div>
            <div className="text-[11px] font-mono text-zinc-500">{wallet ? `${(wallet.transactions || []).length} txns` : ""}</div>
          </div>
          <p className="text-zinc-500 font-mono text-[11px] mb-4">Actions your agents queued for approval. Money actions require a verification code before firing. Nothing real moves — payments run in sandbox.</p>
          {queue.length === 0 && <div className="text-center text-zinc-600 font-mono text-sm py-12">Queue is empty. When an agent (with auto-fire off) completes work, matching actions land here.</div>}
          <div className="space-y-2">
            {queue.map((q) => (
              <div key={q.id} data-testid="queue-item" className="rounded-xl border border-white/10 bg-white/[0.02] p-3">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-lg grid place-items-center shrink-0" style={{ backgroundColor: `${q.color || "#22D3EE"}22`, border: `1px solid ${q.color || "#22D3EE"}55`, color: q.color || "#22D3EE" }}><Icon name={q.icon} size={16} /></div>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm text-zinc-100 truncate font-medium flex items-center gap-2">{q.automation_name}{q.requires_otp && <span className="text-[9px] font-tactical font-bold uppercase text-amber-300 bg-amber-500/10 border border-amber-400/30 rounded px-1.5 py-0.5 flex items-center gap-1"><ShieldCheck size={10} /> Verify</span>}</div>
                    <div className="text-[11px] font-mono text-zinc-500 truncate">by {q.agent_name} · {q.reason}</div>
                  </div>
                  <button data-testid="queue-approve" onClick={() => approve(q)} disabled={busy} className="h-8 px-3 rounded-lg bg-cyan-500/15 hover:bg-cyan-500/25 text-cyan-300 border border-cyan-400/30 text-xs font-tactical font-bold uppercase flex items-center gap-1"><Check size={13} /> Approve</button>
                  <button data-testid="queue-dismiss" onClick={() => dismiss(q)} className="w-8 h-8 grid place-items-center rounded-lg bg-white/5 hover:bg-red-500/10 text-red-400"><Trash2 size={15} /></button>
                </div>
                <pre className="mt-2 text-[10px] font-mono text-zinc-400 bg-black/30 rounded-lg p-2 overflow-x-auto max-h-28 thin-scroll whitespace-pre-wrap">{JSON.stringify(q.payload, null, 2)}</pre>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* CONFIG MODAL */}
      {cfg && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-black/70 backdrop-blur-sm p-4" onClick={() => setCfg(null)}>
          <div className="w-full max-w-md rounded-2xl border border-white/10 bg-[#0b0e14] p-5 anim-slidein" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-11 h-11 rounded-lg grid place-items-center shrink-0" style={{ backgroundColor: `${cfg.color}22`, border: `1px solid ${cfg.color}55`, color: cfg.color }}><Icon name={cfg.icon} size={20} /></div>
              <div><div className="text-zinc-100 font-medium">{cfg.name}</div><div className="text-[10px] uppercase tracking-wider text-zinc-500 font-tactical">{cfg.category}</div></div>
              <button onClick={() => setCfg(null)} className="ml-auto w-8 h-8 grid place-items-center rounded-lg bg-white/5 hover:bg-white/10 text-zinc-400"><X size={16} /></button>
            </div>
            <div className="space-y-3">
              <div>
                <Label className="text-[11px] uppercase tracking-wider text-zinc-400 font-tactical">Name</Label>
                <Input data-testid="config-name" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} className="bg-[#05070b] border-white/10 text-zinc-100" />
              </div>
              <div>
                <Label className="text-[11px] uppercase tracking-wider text-zinc-400 font-tactical">{cfg.url_label}</Label>
                <Input data-testid="config-url" value={form.url} onChange={(e) => setForm((f) => ({ ...f, url: e.target.value }))} placeholder={cfg.url_placeholder} className="bg-[#05070b] border-white/10 text-zinc-100" />
                {cfg.docs_url && <a href={cfg.docs_url} target="_blank" rel="noreferrer" className="text-[10px] text-cyan-400 hover:text-cyan-300 font-mono mt-1 inline-block">Where do I get this? →</a>}
              </div>
              <div>
                <Label className="text-[11px] uppercase tracking-wider text-zinc-400 font-tactical">Note (optional)</Label>
                <Input data-testid="config-desc" value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} className="bg-[#05070b] border-white/10 text-zinc-100" />
              </div>
              {cfg.risk === "high" && <div className="text-[11px] font-mono text-amber-300 bg-amber-500/10 border border-amber-400/25 rounded-lg p-2 flex items-start gap-2"><ShieldCheck size={14} className="mt-0.5 shrink-0" /> Money action — a verification code is required each time before firing. Runs in sandbox; no real funds move.</div>}
              <Button data-testid="config-connect" onClick={connect} disabled={connecting} className="w-full bg-cyan-500 hover:bg-cyan-400 text-[#04070c] font-tactical font-bold uppercase text-xs">{connecting ? <Loader2 size={14} className="animate-spin mr-1" /> : <Plus size={14} className="mr-1" />} Connect</Button>
            </div>
          </div>
        </div>
      )}

      {/* OTP MODAL */}
      {otp && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-black/70 backdrop-blur-sm p-4" onClick={() => setOtp(null)}>
          <div className="w-full max-w-sm rounded-2xl border border-amber-500/30 bg-[#0b0e14] p-5 anim-slidein" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-2 text-amber-300 font-tactical font-bold uppercase tracking-wider text-sm mb-3"><ShieldCheck size={16} /> Verify money action</div>
            <p className="text-xs text-zinc-400 leading-snug mb-3">For your security, re-enter this code to authorize firing <span className="text-zinc-100 font-medium">{otp.label}</span>. This runs in sandbox — no real funds move.</p>
            <div className="text-center text-2xl font-mono tracking-[0.4em] text-amber-200 bg-amber-500/10 border border-amber-400/25 rounded-lg py-3 mb-3" data-testid="otp-code">{otp.code}</div>
            <Input data-testid="otp-input" value={otp.input} onChange={(e) => setOtp((o) => ({ ...o, input: e.target.value }))} placeholder="Enter the code above" className="bg-[#05070b] border-white/10 text-zinc-100 text-center tracking-widest" />
            <div className="flex gap-2 mt-3">
              <Button variant="ghost" onClick={() => setOtp(null)} className="flex-1 text-zinc-400">Cancel</Button>
              <Button data-testid="otp-confirm" onClick={confirmOtp} disabled={busy || otp.input !== otp.code} className="flex-1 bg-amber-500 hover:bg-amber-400 text-[#04070c] font-tactical font-bold uppercase text-xs">{busy ? <Loader2 size={14} className="animate-spin" /> : <><ShieldCheck size={14} className="mr-1" /> Authorize</>}</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
